import type { MoneyValue } from "./types.js";
import { normalizeWhitespace } from "./stable-text.js";

function parseKoreanWon(text: string): number | null {
  const compact = text.replace(/,/gu, "").replace(/\s+/gu, "");
  let total = 0;
  let matched = false;
  const units: Array<[RegExp, number]> = [
    [/(\d+(?:\.\d+)?)억(?:원)?/gu, 100_000_000],
    [/(\d+(?:\.\d+)?)천만원/gu, 10_000_000],
    [/(\d+(?:\.\d+)?)백만원/gu, 1_000_000],
    [/(\d+(?:\.\d+)?)십만원/gu, 100_000],
    [/(\d+(?:\.\d+)?)만원/gu, 10_000],
    [/(\d+(?:\.\d+)?)천원/gu, 1_000],
  ];
  let remainder = compact;
  for (const [pattern, multiplier] of units) {
    remainder = remainder.replace(pattern, (_whole, amount: string) => {
      total += Number(amount) * multiplier;
      matched = true;
      return "";
    });
  }

  const plainWon = remainder.match(/(?:^|[^\d.])(\d+(?:\.\d+)?)원(?:$|[^가-힣])/u) ??
    remainder.match(/^(\d+(?:\.\d+)?)원$/u);
  if (plainWon) {
    total += Number(plainWon[1]);
    matched = true;
  }
  return matched && Number.isFinite(total) ? Math.round(total) : null;
}

function inferKind(text: string): MoneyValue["kind"] {
  if (/최대|이내|한도|상한|까지/u.test(text)) return "maximum";
  if (/최소|이상|하한/u.test(text)) return "minimum";
  if (/정액|고정/u.test(text)) return "fixed";
  if (/%|비율|부담률/u.test(text)) return "ratio";
  return "unknown";
}

export function parseMoneyValue(raw: string | null | undefined): MoneyValue | null {
  if (!raw?.trim()) return null;
  const text = normalizeWhitespace(raw);
  const percentages = [...text.matchAll(/(\d+(?:\.\d+)?)\s*%/gu)].map((match) => Number(match[1]));
  if (percentages.length > 0) {
    const amount = /최소|이상|하한/u.test(text) ? Math.min(...percentages) : Math.max(...percentages);
    return { raw: text, amountKrw: amount, unit: "percent", kind: inferKind(text) };
  }

  const range = text.match(/(\d+(?:\.\d+)?)\s*[~～-]\s*(\d+(?:\.\d+)?)\s*(억원|천만원|백만원|만원|천원|원)/u);
  if (range) {
    const parsed = parseKoreanWon(`${range[2]}${range[3]}`);
    return { raw: text, amountKrw: parsed, unit: parsed === null ? "unknown" : "KRW", kind: "maximum" };
  }

  const amountKrw = parseKoreanWon(text);
  return {
    raw: text,
    amountKrw,
    unit: amountKrw === null ? "unknown" : "KRW",
    kind: inferKind(text),
  };
}
